import React from "react";
import { gaTrackCta } from "../../analytics/ga4";
import { C } from "../../core/constants";
import { localizedPath } from "../../core/localePaths";
import { runtimeConfig } from "../../config/runtime";
import { useI18n } from "../../features/i18n/LocaleProvider";
import Button from "../../ui/Button";
import GlassCard from "../../ui/GlassCard";
import OrbitDeco from "../visual/OrbitDeco";
import { orbitDecoWrapStyle, spotlightCardStyle } from "./landingSpotlightLayout";
import NatalChartDeco from "./NatalChartDeco";

const H3_STYLE = {
  fontFamily: "'Cormorant Garamond', serif",
  fontSize: "clamp(24px, 3vw, 28px)",
  fontWeight: 500,
  color: C.textPrimary,
  lineHeight: 1.25,
  margin: "0 0 16px",
};

const BODY_STYLE = (fs) => ({
  fontSize: fs,
  color: C.textSecondary,
  lineHeight: 1.7,
  margin: "0 0 16px",
});

function SectionBlock({ children, className = "" }) {
  return <div className={`landing-seo__section ${className}`.trim()}>{children}</div>;
}

function CompareList({ items, fs, muted }) {
  return (
    <ul
      className="landing-seo__compare-list"
      style={{
        margin: 0,
        paddingLeft: 20,
        fontSize: fs - 1,
        color: muted ? C.textMuted : C.textSecondary,
        lineHeight: 1.65,
      }}
    >
      {items.map((item, i) => (
        <li key={i} style={{ marginBottom: 8 }}>
          {item}
        </li>
      ))}
    </ul>
  );
}

export default function LandingSeoContent({ acc, t: tProp, isMobile, gutter, fs, setPage }) {
  const { t: tContext, locale } = useI18n();
  const t = tProp || tContext;
  const sectionPad = isMobile ? `0 ${gutter}px` : "0 40px";

  const bridgePills = [
    { icon: "✦", label: t("landing_seo_bridge_pill_1"), accent: acc.primary },
    { icon: "☽", label: t("landing_seo_bridge_pill_2"), accent: acc.secondary },
    { icon: "⊕", label: t("landing_seo_bridge_pill_3"), accent: acc.tertiary },
  ];

  const platformCards = [
    {
      icon: "✦",
      title: t("feature_western_astrology_title"),
      desc: t("feature_western_astrology_desc"),
      page: "western",
      accent: acc.primary,
    },
    {
      icon: "☯",
      title: t("feature_chinese_zodiac_title"),
      desc: t("feature_chinese_zodiac_desc"),
      page: "chinese",
      accent: acc.tertiary,
    },
    {
      icon: "囍",
      title: t("feature_couple_match_title"),
      desc: t("feature_couple_match_desc"),
      page: "coupleMatch",
      accent: acc.primary,
    },
    {
      icon: "⊕",
      title: t("feature_tarot_reading_title"),
      desc: t("feature_tarot_reading_desc"),
      page: "tarot",
      accent: acc.secondary,
    },
    {
      icon: "◈",
      title: t("landing_seo_platform_card_dashboard_title"),
      desc: t("landing_seo_platform_card_dashboard_desc"),
      page: "dashboard",
      accent: acc.primary,
    },
  ];

  const handleSeoSignup = () => {
    if (runtimeConfig.authContext.signedIn) {
      gaTrackCta({
        cta_id: "landing_seo_primary",
        cta_label: t("hero_cta_start_free_reading"),
        destination: "dashboard",
        screen_class: "landing",
      });
      setPage("dashboard");
    } else {
      const destination = localizedPath("signUp", locale);
      gaTrackCta({
        cta_id: "landing_seo_signup",
        cta_label: t("hero_cta_start_free_reading"),
        destination,
        screen_class: "landing",
      });
      window.location.assign(destination);
    }
  };

  const exploreLinkStyle = {
    color: "#a8dfff",
    textDecoration: "none",
    fontSize: fs - 1,
    marginTop: 12,
    display: "inline-block",
    cursor: "pointer",
    background: "none",
    border: "none",
    padding: 0,
    fontFamily: "inherit",
  };

  return (
    <section className="landing-seo" aria-labelledby="landing-on-page-heading">
      <div className="landing-seo__ambient" aria-hidden="true" />
      <div className="landing-seo__inner">
        <div className="landing-seo__bridge">
          <header
            className="landing-seo__bridge-header"
            style={isMobile ? { paddingLeft: gutter, paddingRight: gutter } : undefined}
          >
            <p className="landing-seo__eyebrow" style={{ color: acc.primary }}>
              {t("landing_seo_eyebrow")}
            </p>
            <h2 id="landing-on-page-heading" className="landing-seo__title">
              {t("landing_on_page_title")}
            </h2>
          </header>
          <div
            className="landing-seo__bridge-pills landing-seo__bridge-pills--animate"
            style={isMobile ? { paddingLeft: gutter, paddingRight: gutter } : undefined}
          >
            {bridgePills.map((pill, i) => (
              <GlassCard
                key={i}
                className="landing-seo__pill-card"
                hoverable
                glow
                glowAlpha={0.55}
                accent={pill.accent}
              >
                <span className="landing-seo__pill-icon" style={{ color: pill.accent }} aria-hidden="true">
                  {pill.icon}
                </span>
                <p className="landing-seo__pill-text">{pill.label}</p>
              </GlassCard>
            ))}
          </div>
        </div>

        <SectionBlock>
          <div
            className="landing-seo__two-col"
            style={{ padding: sectionPad, display: "flex", flexDirection: isMobile ? "column" : "row", gap: isMobile ? 32 : 40, alignItems: "center" }}
          >
            <GlassCard style={{ flex: 1, padding: 32, minWidth: 0 }} glow glowAlpha={0.5} accent={acc.primary}>
              <h3 className="landing-seo__h3" style={H3_STYLE}>
                {t("landing_seo_h3_birth_chart")}
              </h3>
              <p className="landing-on-page-copy__paragraph" style={BODY_STYLE(fs)}>
                {t("landing_seo_p1a")}
              </p>
              <p className="landing-on-page-copy__paragraph" style={{ ...BODY_STYLE(fs), marginBottom: 0 }}>
                {t("landing_seo_p1b")}
              </p>
            </GlassCard>
            <NatalChartDeco acc={acc} size={isMobile ? 220 : 280} />
          </div>
        </SectionBlock>

        <SectionBlock>
          <div style={{ padding: sectionPad }}>
            <h3 className="landing-seo__h3" style={{ ...H3_STYLE, textAlign: "center", maxWidth: 720, margin: "0 auto 20px" }}>
              {t("landing_seo_h3_personalized")}
            </h3>
            <p className="landing-on-page-copy__paragraph" style={{ ...BODY_STYLE(fs), maxWidth: 720, margin: "0 auto 28px", textAlign: "center" }}>
              {t("landing_on_page_p2")}
            </p>
            <div className="landing-seo__compare-grid">
              <GlassCard style={{ padding: 28 }} hoverable accent={C.textMuted}>
                <div style={{ fontSize: 28, marginBottom: 12, opacity: 0.5 }}>☉</div>
                <h4
                  style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    fontSize: 22,
                    fontWeight: 500,
                    color: C.textMuted,
                    margin: "0 0 14px",
                  }}
                >
                  {t("landing_seo_compare_generic_title")}
                </h4>
                <CompareList
                  muted
                  fs={fs}
                  items={[t("landing_seo_compare_generic_b1"), t("landing_seo_compare_generic_b2"), t("landing_seo_compare_generic_b3")]}
                />
              </GlassCard>
              <GlassCard style={{ padding: 28 }} hoverable glow glowAlpha={0.45} accent={acc.primary}>
                <div style={{ fontSize: 28, marginBottom: 12, color: acc.primary }}>✦</div>
                <h4
                  style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    fontSize: 22,
                    fontWeight: 500,
                    color: C.textPrimary,
                    margin: "0 0 14px",
                  }}
                >
                  {t("landing_seo_compare_personal_title")}
                </h4>
                <CompareList
                  fs={fs}
                  items={[
                    t("landing_seo_compare_personal_b1"),
                    t("landing_seo_compare_personal_b2"),
                    t("landing_seo_compare_personal_b3"),
                    t("landing_seo_compare_personal_b4"),
                  ]}
                />
              </GlassCard>
            </div>
          </div>
        </SectionBlock>

        <SectionBlock>
          <div style={{ padding: sectionPad }}>
            <div style={{ textAlign: "center", marginBottom: 32 }}>
              <h3 className="landing-seo__h3" style={{ ...H3_STYLE, marginBottom: 16 }}>
                {t("landing_seo_h3_platform")}
              </h3>
              <p className="landing-on-page-copy__paragraph" style={{ ...BODY_STYLE(fs), maxWidth: 720, margin: "0 auto", textAlign: "center" }}>
                {t("landing_on_page_p3")}
              </p>
            </div>
            <div className="landing-seo__platform-grid">
              {platformCards.map((card, i) => (
                <GlassCard
                  key={i}
                  className={`landing-seo__platform-card landing-seo__platform-card--delay-${i}`}
                  style={{ padding: 28 }}
                  hoverable
                  glow
                  accent={card.accent}
                >
                  <div style={{ fontSize: 28, marginBottom: 14, color: card.accent }}>{card.icon}</div>
                  <h4
                    style={{
                      fontFamily: "'Cormorant Garamond', serif",
                      fontSize: 22,
                      fontWeight: 500,
                      color: C.textPrimary,
                      margin: "0 0 10px",
                    }}
                  >
                    {card.title}
                  </h4>
                  <p style={{ fontSize: fs - 1, color: C.textSecondary, lineHeight: 1.65, margin: "0 0 8px" }}>{card.desc}</p>
                  <button
                    type="button"
                    style={exploreLinkStyle}
                    onClick={() => {
                      gaTrackCta({
                        cta_id: `landing_seo_platform_${card.page}`,
                        cta_label: t("landing_seo_platform_explore"),
                        destination: card.page,
                        screen_class: "landing",
                      });
                      setPage(card.page);
                    }}
                  >
                    {t("landing_seo_platform_explore")}
                  </button>
                </GlassCard>
              ))}
            </div>
          </div>
        </SectionBlock>

        <SectionBlock>
          <div style={{ padding: sectionPad }}>
            <GlassCard
              style={spotlightCardStyle(isMobile, { desktopPadding: "56px 48px", desktopGap: 56 })}
              glow
              accent={acc.secondary}
            >
              <div style={{ flex: 1, minWidth: 260 }}>
                <h3 className="landing-seo__h3" style={H3_STYLE}>
                  {t("landing_seo_h3_get_started")}
                </h3>
                <p className="landing-on-page-copy__paragraph" style={BODY_STYLE(fs)}>
                  {t("landing_on_page_p4")}
                </p>
                <Button variant="primary" acc={acc} onClick={handleSeoSignup} style={{ marginTop: 8 }}>
                  {t("hero_cta_start_free_reading")}
                </Button>
              </div>
              <div className="landing-seo__cta-deco" style={orbitDecoWrapStyle(isMobile, { scaleOnMobile: true })}>
                <OrbitDeco acc={acc} variant="natal" />
              </div>
            </GlassCard>
          </div>
        </SectionBlock>
      </div>
    </section>
  );
}
